import { useState, useEffect, useCallback } from 'react';
import { listMyOutlets, fetchConsolidatedSales, type OutletRef, type ConsolidatedRow } from '@/lib/data';

export default function OutletsPage() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
  const [range, setRange] = useState({ from: monthAgo, to: today });
  const [outlets, setOutlets] = useState<OutletRef[]>([]);
  const [rows, setRows] = useState<ConsolidatedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const fromISO = new Date(`${range.from}T00:00`).toISOString();
      const toISO = new Date(`${range.to}T23:59:59`).toISOString();
      const [o, r] = await Promise.all([listMyOutlets(), fetchConsolidatedSales(fromISO, toISO)]);
      setOutlets(o); setRows(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load outlets.');
    } finally { setLoading(false); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;
  const totalGross = rows.reduce((s, r) => s + r.gross, 0);
  const totalOrders = rows.reduce((s, r) => s + r.orders, 0);
  const byId = new Map(rows.map((r) => [r.restaurantId, r]));

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-foreground-900">Outlets</h1>
        <p className="text-sm text-foreground-500">Consolidated sales across all outlets you manage.</p>
      </div>

      <div className="bg-background-50 border border-background-200 rounded-lg p-4 flex flex-wrap gap-3 items-end">
        <label className="block"><span className="text-xs font-medium text-foreground-600">From</span>
          <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
            className="mt-1 px-3 py-2 text-sm border border-background-300 rounded-lg" /></label>
        <label className="block"><span className="text-xs font-medium text-foreground-600">To</span>
          <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
            className="mt-1 px-3 py-2 text-sm border border-background-300 rounded-lg" /></label>
        <button onClick={load} className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer">Refresh</button>
      </div>

      {error && <div className="px-4 py-2 text-sm rounded-lg bg-red-100 text-red-700">{error}</div>}

      {loading ? <p className="text-sm text-foreground-500">Loading…</p> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-background-50 border border-background-200 rounded-lg p-5">
              <p className="text-xs text-foreground-500 uppercase font-semibold">Outlets</p>
              <p className="text-3xl font-bold text-foreground-900 mt-1">{outlets.length}</p>
            </div>
            <div className="bg-background-50 border border-background-200 rounded-lg p-5">
              <p className="text-xs text-foreground-500 uppercase font-semibold">Orders (period)</p>
              <p className="text-3xl font-bold text-foreground-900 mt-1">{totalOrders.toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-background-50 border border-background-200 rounded-lg p-5">
              <p className="text-xs text-foreground-500 uppercase font-semibold">Gross (period)</p>
              <p className="text-3xl font-bold text-primary-600 mt-1">{inr(totalGross)}</p>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-background-200 max-w-3xl">
            <table className="w-full text-sm">
              <thead><tr className="bg-background-100 text-left text-foreground-600">
                <th className="p-3 font-medium">Outlet</th>
                <th className="p-3 font-medium text-right">Orders</th>
                <th className="p-3 font-medium text-right">Gross</th>
              </tr></thead>
              <tbody>
                {outlets.map((o) => {
                  const r = byId.get(o.id);
                  return (
                    <tr key={o.id} className="border-t border-background-100">
                      <td className="p-3 font-medium text-foreground-800">{o.name}</td>
                      <td className="p-3 text-right text-foreground-700">{r ? r.orders.toLocaleString('en-IN') : 0}</td>
                      <td className="p-3 text-right text-foreground-800">{inr(r?.gross ?? 0)}</td>
                    </tr>
                  );
                })}
                {outlets.length === 0 && (
                  <tr><td colSpan={3} className="p-6 text-center text-foreground-400">Only one outlet linked. Link more via the multi-outlet migration.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-foreground-500 max-w-3xl">
            To add another outlet to this owner, insert a row into <code>ezydine.outlet_members</code>
            {' '}(see <code>multi-outlet.sql</code>). Per-outlet day-to-day management still happens inside each outlet's own dashboard.
          </p>
        </>
      )}
    </div>
  );
}
